import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { MapPin, ArrowLeft, Mail, Lock, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  const { login } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Plotëso të gjitha fushat');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success('Mirë se erdhe!');
      navigate(redirectTo || '/llogaria');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Gabim në hyrje. Provo përsëri.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="floating-orb w-96 h-96 bg-violet-500/20 -top-48 -left-48 animate-float" />
        <div className="floating-orb w-64 h-64 bg-cyan-500/20 bottom-20 right-20 animate-float" style={{ animationDelay: '2s' }} />
        <div className="absolute inset-0 grid-pattern opacity-50" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors mb-8"
          data-testid="back-home-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Kthehu në faqen kryesore
        </Link>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center shadow-lg">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-slate-900">TapTag</span>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 text-center mb-2" data-testid="login-title">
            Mirë se erdhe
          </h1>
          <p className="text-slate-600 text-center mb-8">
            Hyr në llogarinë tënde
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="email@shembull.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-11 h-12 border-slate-300 focus:border-violet-500 focus:ring-violet-500"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Fjalëkalimi</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-11 h-12 border-slate-300 focus:border-violet-500 focus:ring-violet-500"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 gradient-primary text-white rounded-full text-lg font-semibold shadow-lg hover:opacity-90 transition-all"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Duke hyrë...
                </>
              ) : (
                'Hyr'
              )}
            </Button>
          </form>

          <p className="text-center text-slate-600 mt-6">
            Nuk ke llogari?{' '}
            <Link to={`/register${redirectTo ? `?redirect=${encodeURIComponent(redirectTo)}` : ''}`} className="text-violet-600 hover:underline font-medium" data-testid="register-link">
              Regjistrohu
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
